import { Injectable, inject } from '@angular/core';
import moment from 'moment/moment';
import { BehaviorSubject } from 'rxjs';
import { Position } from '../../../shared/data-structures/position';
import { CalendarService } from '../services/calendar.service';
import { DateTimeHelper } from '../util/date-time-helper';
import { DSTGapType, DateTimeHelperTZ } from '../util/date-time-helper-tz';
import { MathUtil } from '../util/math';

@Injectable()
export class TimeCoordinateMappingService {
  public pixelsPerMinuteCalculated$ = new BehaviorSubject<number>(null);
  private readonly calendarService = inject(CalendarService);
  private pixelsPerMinute: number | null = null;
  private readonly minutesPerDay = 1440;

  constructor() {
    this.calendarService.calendarViewInitialized$.subscribe((initialized) => {
      if (initialized) {
        const height = this.calendarService.model.geometryData.calendarBodyHeight;
        this.pixelsPerMinute = height / this.minutesPerDay;
        this.pixelsPerMinuteCalculated$.next(this.pixelsPerMinute);
      }
    });
  }

  public toCalendarCoordinate(worldPosition: Position): Position {
    const topOffset = this.calendarService.model.geometryData.calendarBodyOffsetTop;
    const leftOffset = this.calendarService.model.geometryData.calendarBodyOffsetLeft;
    return {
      x: worldPosition.x - leftOffset,
      y: worldPosition.y - topOffset,
    };
  }

  public toDateTime(calendarPositionY: number, ppm: number, referenceDate: Date): Date {
    const minutes = calendarPositionY / ppm;
    // eslint-disable-next-line prefer-const
    let { date, gapType, winterToSummer } = DateTimeHelperTZ.minutesToDate(minutes, referenceDate);
    if (gapType === DSTGapType.AfterGap) {
      date = winterToSummer ? DateTimeHelper.subHours(date, 1) : DateTimeHelper.addHours(date, 1);
    }
    return date;
  }

  /**
   * Calculate the distance between two date times in pixels.
   */
  public mapDurationToScreenLength(start: Date, end: Date): number | never {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startMoment = moment(start).tz(timeZone);
    const endMoment = moment(end).tz(timeZone);

    const elapsedMinutes = endMoment.diff(startMoment, 'minutes');
    const offsetCorrection = endMoment.utcOffset() - startMoment.utcOffset();

    const wallClockMinutes = elapsedMinutes + offsetCorrection;
    const ppm = this.PixelsPerMinute;
    return MathUtil.roundTwoDecimals(wallClockMinutes * ppm);
  }

  public get PixelsPerMinute(): number | null | never {
    if (!this.pixelsPerMinute || isNaN(this.pixelsPerMinute)) {
      throw new Error(
        'Pixels per minute have not been calculated yet. Use the pixelsPerMinuteCalculated$ ' +
          'observable to wait for the calculation to finish.',
      );
    }
    return this.pixelsPerMinute;
  }
}
